import { useMemo } from 'react';

/** Whether Est. Time is enabled in card element settings (shared by mission cards + milestones). */
export function isEstimatedDurationVisible(cardDisplaySettings = {}) {
  return cardDisplaySettings.estimatedDuration === true
    || cardDisplaySettings.estimatedDurationMinutes === true;
}

/** Attached Docs is on unless the card field is explicitly hidden. */
export function isAttachedDocsVisible(cardDisplaySettings = {}) {
  return cardDisplaySettings.attachedDocs !== false;
}

/**
 * useCardDisplaySettings Hook
 * 
 * Computes all display flags for card elements based on cardDisplaySettings prop.
 * Centralizes the "show or hide" logic for every card element.
 * 
 * @param {object} cardDisplaySettings - Settings object controlling element visibility
 * @param {boolean} isDecision - Whether this is a decision node (affects some defaults)
 * @returns {object} All computed show* flags
 */
export const useCardDisplaySettings = (cardDisplaySettings = {}, isDecision = false) => {
  return useMemo(() => {
    // Core elements
    const showMissionIcon = cardDisplaySettings.missionIcon !== false;
    const showStatusDropdown = cardDisplaySettings.statusDropdown !== false;
    const showPriorityDropdown = cardDisplaySettings.priorityDropdown === true; // Off by default
    const showDifficultyDropdown = cardDisplaySettings.difficultyDropdown === true; // Off by default
    const showCategoryDropdown = cardDisplaySettings.categoryDropdown !== false;
    const showBlockedIndicator = cardDisplaySettings.blockedIndicator !== false;
    const showNestedCampaign = cardDisplaySettings.nestedCampaign !== false;
    
    // Toolbar buttons (decisions hide some buttons)
    const showAttachmentsButton = cardDisplaySettings.attachmentsButton !== false && !isDecision; // Hide for decisions
    const showAttachedDocs = isAttachedDocsVisible(cardDisplaySettings);
    const showDetailsButton = cardDisplaySettings.detailsButton !== false;
    const showArchiveButton = cardDisplaySettings.archiveButton === true; // Off by default
    const showDeleteButton = cardDisplaySettings.deleteButton !== false; // On by default
    const showOpenInViewButton = cardDisplaySettings.openInViewButton === true; // Off by default
    
    // Date fields
    const showDueDate = cardDisplaySettings.dueDate !== false; // On by default
    const showEstimatedDuration = isEstimatedDurationVisible(cardDisplaySettings);
    const showCreatedAt = cardDisplaySettings.createdAt === true; // Off by default
    const showStartedAt = cardDisplaySettings.startedAt === true; // Off by default
    const showCompletedAt = cardDisplaySettings.completedAt === true; // Off by default
    const showUpdatedAt = cardDisplaySettings.updatedAt === true; // Off by default
    const showAnyDateField = showDueDate || showEstimatedDuration || showCreatedAt || showStartedAt || showCompletedAt || showUpdatedAt;
    
    return {
      // Core elements
      showMissionIcon,
      showStatusDropdown,
      showPriorityDropdown,
      showDifficultyDropdown,
      showCategoryDropdown,
      showBlockedIndicator,
      showNestedCampaign,
      
      // Toolbar buttons
      showAttachmentsButton,
      showAttachedDocs,
      showDetailsButton,
      showArchiveButton,
      showDeleteButton,
      showOpenInViewButton,
      
      // Date fields
      showDueDate,
      showEstimatedDuration,
      showCreatedAt,
      showStartedAt,
      showCompletedAt,
      showUpdatedAt,
      showAnyDateField,
    };
  }, [cardDisplaySettings, isDecision]);
};

export default useCardDisplaySettings;
